import { ACTIVITIES } from 'queries/query-keys';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import ActivityService from 'services/ActivityService';
import { getBusinessClientObject } from 'store/authentication/selectors';

type Options = {
  activityId: string;
  customObjectId: string;
  enabled?: boolean;
  entityId: string;
  entityLabel: string;
  isClient: boolean;
};

export const useEntityPredefinedOptions = ({
  activityId,
  customObjectId,
  entityId,
  entityLabel,
  isClient,
  enabled = true,
}: Options) => {
  const { data: relationshipData, isLoading: relationshipDataLoading } =
    useQuery({
      queryKey: ACTIVITIES.PRIMARY_ASSOCIATIONS(entityId, activityId),
      queryFn: () => {
        return ActivityService.getPrimaryAssociations(activityId, entityId);
      },
      enabled: Boolean(entityId && activityId && enabled),
    });

  const clientObject = useSelector(getBusinessClientObject);

  const predefinedOptions = useMemo(() => {
    if (relationshipData && customObjectId && entityId) {
      let clientOption;

      const defaultAssignment: Record<string, any> = Object.keys(
        relationshipData.custom_objects
      ).reduce(
        (acc, curr) => {
          if (curr === clientObject.id) {
            const object = relationshipData.custom_objects[curr];
            clientOption = {
              value: relationshipData.custom_objects[curr].entity_record_id,
              label: `${object.first_name ?? ''} ${object.last_name ?? ''} ${
                object.email ? `(${object.email})` : ''
              }`.trim(),
            };
            return acc;
          }

          return {
            ...acc,
            [curr]: {
              label:
                relationshipData.custom_objects[curr].displayName ||
                relationshipData.custom_objects[curr].display_name ||
                relationshipData.custom_objects[curr].name,
              value: relationshipData.custom_objects[curr].entity_record_id,
            },
          };
        },
        {
          objectId: customObjectId,
        }
      );

      if (isClient) {
        clientOption = {
          value: entityId,
          label: entityLabel,
        };
      } else {
        defaultAssignment[customObjectId] = {
          value: entityId,
          label: entityLabel,
        };
      }

      return { defaultAssignment, clientOption };
    }
  }, [
    relationshipData,
    entityId,
    entityLabel,
    customObjectId,
    isClient,
    clientObject,
  ]);

  return {
    predefinedOptions,
    loading: relationshipDataLoading,
  };
};
